import { useRouter } from 'next/router';
import qs from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const CHECKOUT_INTENT_PARAM = 'ldei';

const STRIPE_RETURN_PARAMS = [
  CHECKOUT_INTENT_PARAM,
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_status',
  'setup_intent',
  'setup_intent_client_secret',
];

const FAILED_REDIRECT_STATUS = 'failed';

export type CheckoutRedirectReturnStatus =
  | 'confirming'
  | 'confirmed'
  | 'failed';

export type CheckoutRedirectReturnController = {
  clear: () => void;
  intentId: string | null;
  isReturn: boolean;
  status: CheckoutRedirectReturnStatus | null;
};

type CapturedRedirectReturn = {
  hasReturnParams: boolean;
  intentId: string | null;
  pathname: string;
  query: Record<string, string>;
  redirectStatus: string | null;
};

const readParam = (value: qs.ParsedQuery[string]): string | null => {
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const captureRedirectReturn = (asPath: string): CapturedRedirectReturn => {
  const [pathWithQuery] = asPath.split('#');
  const [pathname] = pathWithQuery.split('?');
  const params = qs.parse(qs.extract(pathWithQuery));
  const query: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    const param = readParam(value);
    if (param && !STRIPE_RETURN_PARAMS.includes(key)) query[key] = param;
  });

  return {
    hasReturnParams: STRIPE_RETURN_PARAMS.some((param) => param in params),
    intentId: readParam(params[CHECKOUT_INTENT_PARAM]),
    pathname,
    query,
    redirectStatus: readParam(params.redirect_status),
  };
};

export const useCheckoutRedirectReturn = (
  confirmEntry: (intentId: string) => Promise<boolean>,
): CheckoutRedirectReturnController => {
  const router = useRouter();
  const [captured] = useState(() => captureRedirectReturn(router.asPath));
  const [status, setStatus] = useState<CheckoutRedirectReturnStatus | null>(
    captured.intentId ? 'confirming' : null,
  );
  const confirmedRef = useRef(false);
  const cleanedRef = useRef(false);

  useEffect(() => {
    if (!captured.intentId || confirmedRef.current) return;
    confirmedRef.current = true;

    if (captured.redirectStatus === FAILED_REDIRECT_STATUS) {
      setStatus('failed');
      return;
    }

    void confirmEntry(captured.intentId).then((confirmed) => {
      setStatus(confirmed ? 'confirmed' : 'failed');
    });
  }, [captured, confirmEntry]);

  useEffect(() => {
    if (!captured.hasReturnParams || cleanedRef.current) return;
    cleanedRef.current = true;

    void router.replace(
      { pathname: captured.pathname, query: captured.query },
      undefined,
      { shallow: true },
    );
  }, [captured, router]);

  const clear = useCallback(() => {
    setStatus(null);
  }, []);

  return useMemo(
    () => ({
      clear,
      intentId: captured.intentId,
      isReturn: captured.intentId !== null,
      status,
    }),
    [captured.intentId, clear, status],
  );
};
